import { useEffect, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { useT } from "../../../i18n";
import { boardPath, roomConsolePath } from "../../../constants/paths";
import { useRoomApi } from "../api/roomApi";

type St =
  | { k: "loading" }
  | { k: "closed" }
  | { k: "open"; roomId: string; down: boolean; modeId: string }
  | { k: "busy" }
  | { k: "error"; code: string };

/** 열린 룸의 서버 상태를 다시 묻는 간격(SR-LCL-04 교사 배너) */
export const ROOM_STATUS_POLL_MS = 10_000;

/**
 * 교사 홈 룸 상태(P2-S1) — 코스 룸 열기·닫기. 여는 동안 제어 평면이 룸 서버를 예열한다(SR-ROOM-01).
 * P2-S2: 룸 서버가 룸을 잃으면(server down) '끊김' + '↻ 다시 연결'(SR-LCL-04 · 학생들은 혼자 놀이터로 옮겨 가 있다).
 * P2-S4: 열려 있으면 '룸 콘솔 →'(FS-11 · /teacher/room/:roomId).
 */
export function useTeacherRoom() {
  const api = useRoomApi();
  const [st, setSt] = useState<St>({ k: "loading" });

  useEffect(() => {
    if (!api) return;
    let alive = true;
    api
      .room()
      .then(
        (r) =>
          alive &&
          setSt(
            r
              ? { k: "open", roomId: r.roomId, down: r.server === "down", modeId: r.modeId }
              : { k: "closed" },
          ),
      )
      // 조회 실패는 '닫힘'으로(열기를 눌러 보면 실제 오류가 보인다)
      .catch(() => alive && setSt({ k: "closed" }));
    return () => {
      alive = false;
    };
  }, [api]);

  // 열린 동안 서버 상태를 다시 묻는다(배너)
  const openId = st.k === "open" ? st.roomId : null;
  useEffect(() => {
    if (!api || !openId) return;
    const h = window.setInterval(() => {
      api
        .room()
        .then((r) =>
          setSt((cur) =>
            cur.k !== "open"
              ? cur
              : r
                ? { k: "open", roomId: r.roomId, down: r.server === "down", modeId: r.modeId }
                : { k: "closed" },
          ),
        )
        .catch(() => {});
    }, ROOM_STATUS_POLL_MS);
    return () => window.clearInterval(h);
  }, [api, openId]);

  /** 룸 열기 — activityId 면 그 활동으로(P2-S5 활동 표 '▶ 룸 열기') · 연 룸(실패면 null) */
  const open = async (activityId?: string) => {
    if (!api) return null;
    setSt({ k: "busy" });
    try {
      const r = await api.open(activityId);
      setSt({ k: "open", roomId: r.roomId, down: r.server === "down", modeId: r.modeId });
      return r;
    } catch (e) {
      setSt({ k: "error", code: toApiError(e).code });
      return null;
    }
  };
  const close = async () => {
    if (!api) return;
    setSt({ k: "busy" });
    try {
      await api.close();
      setSt({ k: "closed" });
    } catch (e) {
      setSt({ k: "error", code: toApiError(e).code });
    }
  };
  /** 모드 전환 — 성공하면 칸에 반영(학생 화면은 룸 서버가 새 FullState 로 바꾼다) */
  const setMode = async (modeId: string) => {
    if (!api || st.k !== "open") return;
    try {
      const r = await api.setMode(modeId);
      setSt((cur) =>
        cur.k === "open" ? { ...cur, modeId: r.modeId === "lobby" ? "gallery" : r.modeId } : cur,
      );
    } catch (e) {
      setSt({ k: "error", code: toApiError(e).code });
    }
  };

  return { st, open, close, setMode };
}

export type TeacherRoom = ReturnType<typeof useTeacherRoom>;

/**
 * 교사 홈 룸 상태 칸(화면 설계 설명서 2.1 ⑥ · 보완 v0.1 2.5) — 닫힘 · 여는 중 · 열림 · 끊김. 10초마다 새로 확인(useTeacherRoom).
 *  - 열림: '룸 콘솔 →' · '📺 칠판 뷰'(새 창 — 룸 콘솔의 칠판 단추와 같은 창 이름)
 *  - 끊김(룸 서버가 룸을 잃음): 빨간 칩 + 흰 테두리 '↻ 다시 연결'(열기와 같은 호출 · 멱등) — 큰 버튼은 오늘 수업 카드의 하나뿐
 *  - 여는 데 실패: 오류 코드 한 줄
 * 모드 바꾸기 · 룸 닫기는 룸 콘솔에서(설명서 2.1 H).
 */
export function TeacherRoomCell({ room }: { room: TeacherRoom }) {
  const t = useT();
  const { st, open } = room;
  const down = st.k === "open" && st.down;
  const label = down
    ? t("teacher.home.roomDown")
    : st.k === "open"
      ? t("teacher.home.roomOpenNow")
      : st.k === "busy"
        ? t("teacher.home.roomOpening")
        : st.k === "loading"
          ? "—"
          : t("teacher.home.roomClosed");
  return (
    <section
      className="pf-pane pf-roomcell"
      aria-labelledby="roomcell-title"
      data-testid="room-kpi"
      data-room={st.k}
      data-server={st.k === "open" ? (st.down ? "down" : "up") : undefined}
    >
      <h2 id="roomcell-title" className="pf-muted pf-roomcell__title">
        {t("teacher.home.kpiRoom")}
      </h2>
      <span
        className={`pf-chip-static pf-roomcell__state pf-chip-static--${down ? "err" : st.k === "open" ? "ok" : st.k === "busy" ? "run" : "info"}`}
        data-testid="room-state"
      >
        {label}
      </span>
      {st.k === "error" && (
        <p className="pf-muted" role="alert" data-code={st.code}>
          {t("teacher.home.roomFailed")} · {st.code}
        </p>
      )}
      {down && (
        <Button
          variant="ghost"
          className="pf-btn--sm"
          onClick={() => void open()}
          data-testid="reconnect-room"
        >
          {t("teacher.home.reconnectRoom")}
        </Button>
      )}
      {st.k === "open" && (
        <div className="pf-roomcell__links">
          <ButtonLink
            to={roomConsolePath(st.roomId)}
            variant="ghost"
            className="pf-btn--sm"
            data-testid="room-console-link"
          >
            {t("teacher.home.roomConsole")}
          </ButtonLink>
          <a
            href={boardPath(st.roomId)}
            target={`pf-board-${st.roomId}`}
            className="pf-btn pf-btn--ghost pf-btn--sm"
            data-testid="room-board-link"
            title={t("teacher.room.boardHint")}
            onClick={(e) => {
              const w = window.open(
                boardPath(st.roomId),
                `pf-board-${st.roomId}`,
                "popup,width=1280,height=720",
              );
              if (w) e.preventDefault();
            }}
          >
            {t("teacher.home.board")}
          </a>
        </div>
      )}
    </section>
  );
}
